import { z } from 'zod';
import { request, useQuery } from '@applyboard/ui-utils';

const STUDENTS_V2_URL = '/api/v2/students';

const STUDENT_FIELDS = ['first_name', 'last_name', 'email', 'whatsapp_phone_number'];

const BASE_STUDENT_V2_QUERY = `${STUDENTS_V2_URL}?fields[student]=${STUDENT_FIELDS.join(',')}`;

const RawStudentSchema = z.object({
  id: z.string(),
  type: z.literal('student'),
  attributes: z.object({
    first_name: z.string(),
    last_name: z.string(),
    email: z.string().email(),
    whatsapp_phone_number: z.string().nullish(),
  }),
});

export type RawStudent = z.infer<typeof RawStudentSchema>;

const RawStudentDataSchema = z.object({
  data: z.array(RawStudentSchema),
});

export type RawStudentData = z.infer<typeof RawStudentDataSchema>;

export interface useGetStudentsProps {
  orFilter: {
    studentId?: string;
    studentNameFilter?: string;
  };
}

export function useGetStudents(
  {
    orFilter,
    orFilter: { studentId: orStudentIdFilter, studentNameFilter: orStudentNameFilter },
  }: useGetStudentsProps,
  {
    enabled,
    retryCount,
    retryOnMount,
  }: { enabled?: boolean; retryCount?: number; retryOnMount?: boolean } = { retryCount: 2 },
) {
  const { isLoading, isFetching, error, data } = useQuery(
    ['useGetStudents', orFilter, { pkg: 'ab-vas-gic' }],
    async () => {
      const promises: Promise<RawStudentData>[] = [];

      // Search by student Id
      if (orStudentIdFilter) {
        promises.push(
          request<RawStudentData>(`${BASE_STUDENT_V2_QUERY}&filter[id]=${orStudentIdFilter}`, {
            schema: RawStudentDataSchema,
            allowAnonymous: false,
          }),
        );
      }

      const trimmedStudentNameFilter = orStudentNameFilter?.trim() ?? '';
      // Search by first name or last name
      if (orStudentNameFilter) {
        promises.push(
          request<RawStudentData>(
            `${BASE_STUDENT_V2_QUERY}&${trimmedStudentNameFilter
              .split(' ')
              .map(name => `filter[first_name]=${name}`)
              .join('&')}`,
            {
              schema: RawStudentDataSchema,
              allowAnonymous: false,
            },
          ),
        );
        promises.push(
          request<RawStudentData>(
            `${BASE_STUDENT_V2_QUERY}&${trimmedStudentNameFilter
              .split(' ')
              .map(name => `filter[last_name]=${name}`)
              .join('&')}`,
            {
              schema: RawStudentDataSchema,
              allowAnonymous: false,
            },
          ),
        );
      }

      const responses = await Promise.all(promises);

      const combinedResponses = responses.reduce(
        (acc, response) => {
          response.data.forEach(student => {
            acc[student.id] = student;
          });
          return acc;
        },
        {} as Record<string, RawStudent>,
      );

      return Object.values(combinedResponses);
    },
    {
      enabled: enabled,
      refetchOnMount: false,
      retryOnMount: retryOnMount,
      retry: retryCount,
    },
  );

  return {
    students: data,
    studentsError: error,
    isLoadingStudents: isLoading,
    isFetchingStudents: isFetching,
  };
}
